import {Subscription} from 'rxjs';
import {Component, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {AbstractControl, AbstractControlDirective, ValidationErrors} from '@angular/forms';
import {ErrorMessage, UntranslatedErrorMessage} from '@app/layout/rules/template-editor/api/template-editor-node-properties';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {I18nMessage} from '@shared/api/model/i18n';


@Component({
  selector: 'app-errors',
  templateUrl: './errors.component.html',
  styleUrls: ['./errors.component.scss'],
  standalone: false
})
export class ErrorsComponent implements OnChanges, OnDestroy {

  @Input()
  control: AbstractControl | AbstractControlDirective;

  @Input()
  errors: ValidationErrors;

  @Input()
  translatedErrorMessagesByType: {[type: string]: I18nMessage};

  @Input()
  numberToShow = 1;

  private readonly REQUIRED_ERROR_KEY = 'required';
  private readonly logger: AceLogger;

  private previousValidationErrors: ValidationErrors = {};
  private controlStatusChangeSubscription: Subscription;

  errorsForDisplay: ErrorMessage[] | UntranslatedErrorMessage[] = [];

  constructor(logService: AceLoggerService) {
    this.logger = logService.create(ErrorsComponent.name);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      this.recalculateErrors();

      if (changes.control) {
        this.cancelControlStatusChangeSubscription();
        if (this.control) {
          this.controlStatusChangeSubscription = this.control.statusChanges.subscribe(() => this.recalculateErrors());
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.cancelControlStatusChangeSubscription();
  }

  showControl(): boolean {
    return (this.control && this.control.invalid && (this.control.dirty || this.control.touched))
      || (this.errors && Object.keys(this.errors).length > 0);
  }

  private recalculateErrors(): void {
    /**
     * errors coming from controls can have different formats
     * 1. { [validationType]: 'errorMessage', ... }
     * 2. { [fieldName]: { [validationType]: 'errorMessage', ... } }
     */
    let errorsToProcess: ValidationErrors;
    if (this.control) {
      errorsToProcess = this.control.errors ?? {};
    } else if (this.errors) {
      errorsToProcess = this.errors;
    } else {
      errorsToProcess = {};
    }

    if (this.compareErrors(this.previousValidationErrors, errorsToProcess)) {
      const errs = [];

      if (errorsToProcess.required) {
        const requiredErrorMessage = this.getErrorMessageByType(this.REQUIRED_ERROR_KEY);
        errs.push(requiredErrorMessage ? requiredErrorMessage : { messageKey: this.REQUIRED_ERROR_KEY, parameters: {} });
        if (errs.length >= this.numberToShow) {
          this.previousValidationErrors = errorsToProcess;
          this.errorsForDisplay = errs;
          return;
        }
      }

      let i = 0;
      const errorKeys = Object.keys(errorsToProcess).filter(key => !([this.REQUIRED_ERROR_KEY].includes(key)));
      while (i < errorKeys.length && errs.length < this.numberToShow) {
        const errorKey = errorKeys[i];
        const error = errorsToProcess[errorKey];
        if (error.messageKey) {
          let parameters = error.parameters;
          if (!parameters) {
            parameters = {};
          }
          errs.push({messageKey: error.messageKey, parameters: error.parameters});
        } else {
          let logUntranslatedError = false;
          if (typeof error === 'object') {
            if (error.messageText) {
              logUntranslatedError = true;
              errs.push(error);
            } else {
              const overriddenErrorMessage = this.getErrorMessageByType(errorKey, error);
              if (overriddenErrorMessage) {
                errs.push(overriddenErrorMessage);
              } else {
                for (const k of Object.keys(error)) {
                  const overriddenErrorMessage = this.getErrorMessageByType(k);
                  if (overriddenErrorMessage) {
                    errs.push(overriddenErrorMessage);
                  } else {
                    const errorObject = error[k];
                    if (errorObject.messageKey) {
                      errs.push(errorObject);
                    } else {
                      errs.push({messageText: k + ': ' + JSON.stringify(errorObject)});
                    }
                  }
                  logUntranslatedError = logUntranslatedError || !overriddenErrorMessage;
                }
              }
            }
          } else {
            const translatedErrorMessage = this.getErrorMessageByType(errorKey) ?? this.getErrorMessageByType(error);
            errs.push(translatedErrorMessage ? translatedErrorMessage : { messageText: error });
            logUntranslatedError = logUntranslatedError || !translatedErrorMessage;
          }
          if (logUntranslatedError) {
            this.logger.debug('WARNING: Untranslated error message: ' + error);
          }
        }
        i++;
      }
      this.previousValidationErrors = errorsToProcess;
      this.errorsForDisplay = errs;
    }
  }

  private compareErrors(oldErrors, newErrors): boolean {
    const oldKeys = Object.keys(oldErrors);
    const newKeys = Object.keys(newErrors);

    if (oldKeys.length === newKeys.length) {
      for (const key of oldKeys) {
        const oldError = oldErrors[key];
        const newError = newErrors[key];
        if (oldError.messageKey) {
          if (newError && oldErrors.messageKey === newError.messageKey) {
            if (oldError.parameters) {
              const oldParamKeys = Object.keys(oldError.parameters);
              if (newError.parameters) {
                const newParamKeys = Object.keys(newError.parameters);
                if (oldParamKeys.length === newParamKeys.length) {
                  for (const paramKey of oldParamKeys) {
                    if (oldError.parameters[paramKey] !== newError.parameters[paramKey]) {
                      return true;
                    }
                  }
                } else {
                  return true;
                }
              } else if (oldParamKeys.length) {
                return true;
              }
            } else if (newError.parameters && Object.keys(newError.parameters).length) {
              return true;
            }
          } else {
            return true;
          }
        } else if (oldError !== newError) {
          return true;
        }
      }
      return false;
    } else {
      return true;
    }
  }

  private getErrorMessageByType(type: string, params?: {[k: string]: any}): I18nMessage {
    const overriddenErrorMessage = this.translatedErrorMessagesByType?.[type];
    if (overriddenErrorMessage) {
      if (params != null) {
        const mergedParams = Object.assign({}, params, overriddenErrorMessage.parameters);
        return {...overriddenErrorMessage, parameters: mergedParams};
      } else {
        return overriddenErrorMessage;
      }
    } else {
      return null;
    }
  }

  private cancelControlStatusChangeSubscription() {
    if (this.controlStatusChangeSubscription != null) {
      this.controlStatusChangeSubscription.unsubscribe();
      this.controlStatusChangeSubscription = null;
    }
  }
}
